import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, PenLine } from "lucide-react";
import { API } from "@/lib/api";
import Seo from "@/components/Seo";
import InquiryDrawer from "@/components/InquiryDrawer";
import { Reveal, EASE } from "@/components/Reveal";

const STATUS_STYLES = {
  "Available in Studio": "text-sage border-sage/40 bg-sage/10",
  "Reserved for Exhibition": "text-ochre border-ochre/40 bg-ochre/10",
  "In Private Collection": "text-stone2 border-stone2/40 bg-stone2/10",
};

export default function ArtworkDetail() {
  const { id } = useParams();
  const [art, setArt] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [inquireOpen, setInquireOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArt(null);
    setNotFound(false);
    window.scrollTo(0, 0);
    API.get(`/artworks/${id}`).then((r) => setArt(r.data)).catch(() => setNotFound(true));
    API.get("/artworks").then((r) => setSiblings(r.data.filter((a) => a.id !== id).slice(0, 3))).catch(() => {});
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-5 sm:px-10 py-32 text-center" data-testid="artwork-not-found">
        <p className="font-serif text-4xl text-ink">This work has left the studio wall.</p>
        <Link to="/portfolio" className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-terracotta">
          <ArrowLeft size={14} /> Back to the portfolio
        </Link>
      </div>
    );
  }

  if (!art) {
    return <div className="max-w-7xl mx-auto px-5 sm:px-10 py-32 font-mono text-xs uppercase tracking-[0.25em] text-stone2" data-testid="artwork-loading">Carrying the canvas in…</div>;
  }

  const specs = [
    ["Medium", art.medium],
    ["Dimensions", art.dimensions],
    art.frame ? ["Frame", art.frame] : null,
    ["Year", art.year],
    ["Series", art.series],
  ].filter(Boolean);

  return (
    <div data-testid="artwork-detail-page">
      <Seo title={art.title} description={`${art.title} — ${art.medium}, ${art.dimensions}. Original landscape painting by Richard Kuensting.`} />
      <div className="max-w-7xl mx-auto px-5 sm:px-10 pt-8 sm:pt-12 pb-24">
        <Link to="/portfolio" data-testid="artwork-back-link"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-stone2 hover:text-terracotta transition-colors">
          <ArrowLeft size={13} /> All works
        </Link>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mt-8">
          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}
          >
            <div className="bg-paper border border-ink/10 p-3 sm:p-6 deckle spotlight">
              <img src={art.image} alt={`${art.title} — ${art.medium} by Richard Kuensting`}
                className="w-full object-cover border border-ink/10" data-testid="artwork-detail-image" />
            </div>
            {art.palette?.length > 0 && (
              <div className="mt-5 flex items-center gap-3" data-testid="artwork-palette">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2">Palette</span>
                <div className="flex gap-2">
                  {art.palette.map((hex) => (
                    <span key={hex} title={hex} className="w-7 h-7 rounded-full border border-ink/15" style={{ backgroundColor: hex }} />
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sage">{art.series} · {art.year}</p>
              <h1 className="font-serif font-light text-4xl sm:text-5xl text-ink mt-3 leading-[1.05]" data-testid="artwork-detail-title">{art.title}</h1>
              <span className={`inline-block mt-5 font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border ${STATUS_STYLES[art.status] || ""}`} data-testid="artwork-status">
                {art.status}
              </span>
              <p className="text-stone2 leading-relaxed mt-6 text-sm sm:text-base">{art.description}</p>

              <dl className="mt-8 border-t border-ink/10">
                {specs.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-6 py-3 border-b border-ink/10">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone2 pt-0.5">{k}</dt>
                    <dd className="text-sm text-ink text-right">{v}</dd>
                  </div>
                ))}
              </dl>

              <p className="font-serif text-3xl text-ink mt-8" data-testid="artwork-price">{art.price_display}</p>

              <button
                data-testid="inquire-modal-trigger"
                onClick={() => setInquireOpen(true)}
                disabled={art.status === "In Private Collection"}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-ink text-linen rounded-full py-4 text-xs font-mono uppercase tracking-[0.2em] hover:bg-terracotta transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PenLine size={14} />
                {art.status === "In Private Collection" ? "In a Private Collection" : "Inquire on this Work"}
              </button>
              <p className="text-[11px] text-stone2 mt-4 leading-relaxed text-center">
                Worldwide crated shipping · Certificate of provenance · 14-day studio approval period
              </p>
            </Reveal>
          </div>
        </div>

        {siblings.length > 0 && (
          <section className="mt-24 border-t border-ink/10 pt-14">
            <div className="flex items-end justify-between gap-6 mb-10">
              <h2 className="font-serif text-2xl sm:text-3xl text-ink">Also on the wall</h2>
              <Link to="/portfolio" className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-stone2 hover:text-terracotta transition-colors">
                View all <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {siblings.map((s) => (
                <Link key={s.id} to={`/artwork/${s.id}`} data-testid={`artwork-sibling-${s.id}`} className="group">
                  <div className="overflow-hidden bg-cream border border-ink/10 aspect-[5/4] deckle">
                    <img src={s.image} alt={s.title} loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                  </div>
                  <p className="font-serif text-lg text-ink mt-3 leading-tight">{s.title}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone2 mt-1">{s.series}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
      <InquiryDrawer open={inquireOpen} onClose={() => setInquireOpen(false)} artwork={art} />
    </div>
  );
}
